import { CloseIcon } from '@change/ds-icons';

import { Button, ThemeProvider } from '@change/ds-core-ui/next';

import { TrackingContext, useTracking } from '@change-corgi/core/react/tracking';
import { DesignSystemLegacy } from '@change-corgi/design-system-legacy/core';
import { Box, Container } from '@change-corgi/design-system-legacy/layout';
import type { ModalBodyProps, ModalDecoratorProps } from '@change-corgi/design-system-legacy/modals';
import { createModalHook } from '@change-corgi/design-system-legacy/modals';
import { sunriseTheme } from '@change-corgi/design-system-legacy/theme';

import { StripeLoader } from 'src/app/shared/components/payments';
import { PaypalProvider } from 'src/app/shared/hooks/payments';

import { Footer } from './components/Footer';
import { GeneralErrorMessage } from './components/GeneralErrorMessage';
import { Header } from './components/Header';
import { VariantWrapper } from './components/SlideUp/VariantWrapper';
import { MembershipForm } from './container/MembershipForm';
import { MembershipModalStateContextProvider, useMembershipModalState } from './modalContext/modalState';
import type { ModalOptions, TrackingProperties } from './shared/types';
import { SunriseHoldoutExperimentContextProvider } from './sunriseHoldoutExperiment/components/SunriseHoldoutExperimentContextProvider';
import { useSunriseHoldoutExperiment } from './sunriseHoldoutExperiment/hooks/useSunriseHoldoutExperiment';

type Options = ModalOptions & {
	properties: TrackingProperties;
	sunriseDoubleHoldoutExperiment?: { variant: string | undefined };
} & { ppmuExperimentVariant?: string };

type Result = { accepted: boolean; amount: number; reason?: string };

export type MembershipAskModalOptions = Options;

const decorator = (props: ModalDecoratorProps<Options, unknown>): React.JSX.Element => {
	const {
		children,
		options: { properties, sunriseDoubleHoldoutExperiment, ...modalState },
	} = props;
	return (
		<MembershipModalStateContextProvider {...modalState}>
			<TrackingContext properties={properties}>
				<StripeLoader>
					<PaypalProvider>
						<SunriseHoldoutExperimentContextProvider variant={sunriseDoubleHoldoutExperiment?.variant}>
							{children}
						</SunriseHoldoutExperimentContextProvider>
					</PaypalProvider>
				</StripeLoader>
			</TrackingContext>
		</MembershipModalStateContextProvider>
	);
};

function MembershipModalBody({
	closeModal,
	options,
}: ModalBodyProps<Options, Result, undefined>): React.JSX.Element | null {
	const [{ amount }] = useMembershipModalState();
	const { isSunriseEnabled } = useSunriseHoldoutExperiment({ isSunriseEnabled: options.isSunriseEnabled });

	return (
		<DesignSystemLegacy theme={isSunriseEnabled ? sunriseTheme : undefined}>
			<Container sx={{ pt: ['0%', '5%', '5%'] }}>
				<Box sx={{ m: 'auto', px: [0, 32], width: ['100%', '80%', '50%'] }}>
					<Header />
					<MembershipForm
						onAccept={() => closeModal({ accepted: true, amount })}
						onDecline={() => closeModal({ accepted: false, amount })}
					/>
					<GeneralErrorMessage />
					<Footer />
				</Box>
			</Container>
		</DesignSystemLegacy>
	);
}

export const useMembershipAskModal = createModalHook<Options, Result>({
	name: 'MembershipModal',
	closeButton: false,
	closeOnEscape: false,
	closeOnClickOutside: false,
	variant: 'fullPage',
	body: MembershipModalBody,
	decorator,
});

const ExperimentModalBody = ({
	closeModal,
	options: { ppmuExperimentVariant = 'control' },
}: ModalBodyProps<Options, Result, undefined>): React.JSX.Element | null => {
	const [{ amount }] = useMembershipModalState();
	const track = useTracking();

	const handleClose = () => {
		void track('membership_ask_slide_up_close');
		closeModal({ accepted: false, amount });
	};

	const displayCloseButton = ppmuExperimentVariant === 'variant_4';

	return (
		<ThemeProvider>
			{displayCloseButton && (
				<div className="!absolute right-3 top-3 flex h-8 items-center justify-center p-0">
					<Button
						type="button"
						aria-label="Close button"
						className="rounded-full p-1 no-underline"
						iconOnly
						leadingIcon={<CloseIcon />}
						variant="tertiary"
						onClick={handleClose}
					/>
				</div>
			)}
			<VariantWrapper
				variant={ppmuExperimentVariant}
				onAccept={() => closeModal({ accepted: true, amount })}
				onDecline={() => closeModal({ accepted: false, amount })}
				onDeclineWithReason={() => closeModal({ accepted: false, amount, reason: 'navigate_to_member_join' })}
			/>
		</ThemeProvider>
	);
};

type Props = {
	variant: 'default' | 'fullPage';
};
const createExperimentModalHook = ({ variant }: Props) => {
	return createModalHook<Options, Result>({
		name: 'MembershipExperimentModal',
		closeButton: false,
		closeOnEscape: true,
		closeOnClickOutside: true,
		variant,
		body: ExperimentModalBody,
		decorator,
	});
};

export const usePPMUExperimentModal = createExperimentModalHook({ variant: 'default' });
export const usePPMUFullPageExperimentModal = createExperimentModalHook({ variant: 'fullPage' });
